import { Component, EventEmitter, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { TranslocoModule } from '@jsverse/transloco';
import { DateTime } from 'luxon';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterFieldOperator } from '@ukam/core/filters/types/filter-field-operator.type';
import { provideAppDateAdapter } from '@ukam/core/date-adapter/app-date-adapter.provider';
import { FilterField } from '@ukam/core/filters/models/filter-field';

interface DateTimeFilterOperatorOption {
  value: FilterFieldOperator;
  label: string;
}

const DATETIME_FILTER_OPERATORS: DateTimeFilterOperatorOption[] = [
  { value: 'today', label: 'Today' },
  { value: 'eq', label: 'Equal to' },
  { value: 'ne', label: 'Not equal to' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater than or equal to' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less than or equal to' },
  { value: 'btw', label: 'Between' },
];

@Component({
  selector: 'app-datetime-filter',
  templateUrl: './datetime-filter.component.html',
  imports: [FormsModule, MaterialModule, TranslocoModule, CdkDragHandle],
  providers: [
    provideAppDateAdapter(),
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppDateTimeFilterComponent), multi: true },
  ],
})
export class AppDateTimeFilterComponent implements ControlValueAccessor {
  @Output() removed = new EventEmitter<void>();

  operators = DATETIME_FILTER_OPERATORS;

  field: FilterField | null = null;
  operator: FilterFieldOperator | string | undefined;

  value: DateTime | null = null;
  valueTime = '';
  start: DateTime | null = null;
  startTime = '';
  end: DateTime | null = null;
  endTime = '';

  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(400)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  get isToday(): boolean {
    return this.operator === 'today';
  }

  get isRange(): boolean {
    return this.operator === 'btw';
  }

  onOperatorChange(op: FilterFieldOperator | string | undefined): void {
    const patch: Partial<FilterField> = { operator: op };

    if (op === 'btw') {
      patch.value = undefined;
    } else if (op === 'today') {
      patch.value = undefined;
      patch.start = undefined;
      patch.end = undefined;
    } else {
      patch.start = undefined;
      patch.end = undefined;
    }

    this.operator = op;
    this.applyValue(patch.value);
    this.applyStart(patch.start);
    this.applyEnd(patch.end);
    this.emit(patch);
  }

  onValueDateChange(date: DateTime | null): void {
    this.value = date;
    this.emit({ value: this.combine(this.value, this.valueTime) });
  }

  onValueTimeChange(time: string): void {
    this.valueTime = time;
    this.emit({ value: this.combine(this.value, this.valueTime) });
  }

  onStartDateChange(date: DateTime | null): void {
    this.start = date;
    this.emit({ start: this.combine(this.start, this.startTime) });
  }

  onStartTimeChange(time: string): void {
    this.startTime = time;
    this.emit({ start: this.combine(this.start, this.startTime) });
  }

  onEndDateChange(date: DateTime | null): void {
    this.end = date;
    this.emit({ end: this.combine(this.end, this.endTime) });
  }

  onEndTimeChange(time: string): void {
    this.endTime = time;
    this.emit({ end: this.combine(this.end, this.endTime) });
  }

  clear(): void {
    this.applyValue(undefined);
    this.applyStart(undefined);
    this.applyEnd(undefined);
    this.emit({ value: undefined, start: undefined, end: undefined });
  }

  private applyValue(raw: any): void {
    const [date, time] = this.parse(raw);
    this.value = date;
    this.valueTime = time;
  }

  private applyStart(raw: any): void {
    const [date, time] = this.parse(raw);
    this.start = date;
    this.startTime = time;
  }

  private applyEnd(raw: any): void {
    const [date, time] = this.parse(raw);
    this.end = date;
    this.endTime = time;
  }

  // The backend stores UTC. `parse` converts the incoming UTC value to the browser's local
  // time zone for display, and `combine` converts the locally edited date+time back to UTC.
  private parse(raw: string | undefined | null): [DateTime | null, string] {
    if (!raw) return [null, ''];

    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(raw);
    if (match) {
      const [, year, month, day, hour, minute] = match;
      const hasTime = hour !== undefined && minute !== undefined;
      const utc = DateTime.utc(Number(year), Number(month), Number(day), hasTime ? Number(hour) : 0, hasTime ? Number(minute) : 0);
      const local = utc.toLocal();
      const date = local.startOf('day');
      const time = hasTime ? local.toFormat('HH:mm') : '';
      return [date, time];
    }

    const date = DateTime.fromISO(raw);
    if (!date.isValid) return [null, ''];

    return [date.startOf('day'), date.toFormat('HH:mm')];
  }

  private combine(date: DateTime | null, time: string): string | undefined {
    if (!date) return undefined;

    const [hh, mm] = time ? time.split(':') : ['00', '00'];
    const local = DateTime.local(date.year, date.month, date.day, Number(hh), Number(mm), 0);

    return local.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:00");
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.operator = this.field?.operator;
    this.applyValue(this.field?.value);
    this.applyStart(this.field?.start);
    this.applyEnd(this.field?.end);
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
